// Toutes les écritures passent par les RPC (§3.1) — jamais d'UPDATE direct sur
// `journees`, jamais de réécriture du blob `rapport` depuis le client.
// Ce fichier est la seule frontière réseau de l'application.

import { supabase } from '@/lib/supabase'
import type {
  Run, Etape, Journee, EvenementJournal, Message, Consigne, PadAttente, SessionHw,
} from '@/domain/types'
import type { Apercu, SessionEnAttente } from '@/domain/hw'

async function rpc<T>(nom: string, args: Record<string, unknown> = {}): Promise<T> {
  const { data, error } = await supabase.rpc(nom, args)
  if (error) throw error
  return data as T
}

// ── Lecture ────────────────────────────────────────────────────────────────

export interface JourneeLegere { date: string; status: Journee['status'] }

export async function listerJournees(depuis: string, jusqua: string): Promise<JourneeLegere[]> {
  const { data, error } = await supabase
    .from('journees').select('date, status')
    .gte('date', depuis).lte('date', jusqua)
    .order('date', { ascending: false })
  if (error) throw error
  return (data ?? []) as JourneeLegere[]
}

export async function listerRuns(depuis: string, jusqua: string): Promise<Run[]> {
  const { data, error } = await supabase
    .from('fc_runs').select('*')
    .gte('journee_date', depuis).lte('journee_date', jusqua)
    .order('journee_date', { ascending: false })
  if (error) throw error
  return (data ?? []) as Run[]
}

/**
 * Chargement unique à l'entrée dans la run (§2.1) : la journée entière arrive
 * en une fois, puis la navigation se fait en mémoire.
 */
export async function chargerJournee(date: string): Promise<Journee | null> {
  const { data, error } = await supabase
    .from('journees').select('*').eq('date', date).maybeSingle()
  if (error) throw error
  return (data ?? null) as Journee | null
}

/** Les journées (avec leur blob rapport) sur une fenêtre — pour la remise. */
export async function chargerJourneesRemise(
  start: string, fin: string,
): Promise<Array<{ date: string; status: string; rapport: Record<string, unknown> | null }>> {
  const { data, error } = await supabase
    .from('journees').select('date, status, rapport')
    .gte('date', start).lte('date', fin).order('date')
  if (error) throw error
  return (data ?? []) as Array<{ date: string; status: string; rapport: Record<string, unknown> | null }>
}

export async function chargerRun(date: string): Promise<Run | null> {
  const { data, error } = await supabase
    .from('fc_runs').select('*').eq('journee_date', date).maybeSingle()
  if (error) throw error
  return (data ?? null) as Run | null
}

export async function chargerEtapes(date: string): Promise<Etape[]> {
  const { data, error } = await supabase
    .from('fc_etapes').select('*').eq('run_date', date).order('rang')
  if (error) throw error
  return (data ?? []) as Etape[]
}

export async function chargerJournal(date: string, limite = 400): Promise<EvenementJournal[]> {
  const { data, error } = await supabase
    .from('fc_journal').select('*').eq('run_date', date)
    .order('at', { ascending: false }).limit(limite)
  if (error) throw error
  return (data ?? []) as EvenementJournal[]
}

export async function chargerSessionsHw(date: string): Promise<SessionHw[]> {
  const { data, error } = await supabase
    .from('fc_hw_sessions').select('*').eq('run_date', date)
    .order('ouverte_at', { ascending: false })
  if (error) throw error
  return (data ?? []) as SessionHw[]
}

// ── Le parcours ────────────────────────────────────────────────────────────

export const ouvrirRun = (date: string) =>
  rpc<{ run: Run; regles: Record<string, string>; creee: boolean }>('fc_open_run', { p_date: date })

export const prendreMain = (date: string, force = false) =>
  rpc<Run>('fc_prendre_main', { p_date: date, p_force: force })

export const libererRun = (date: string) => rpc<Run>('fc_liberer', { p_date: date })

export const battement = (date: string) => rpc<Run>('fc_heartbeat', { p_date: date })

export const activerEtape = (date: string, code: string) =>
  rpc<Etape>('fc_etape_activer', { p_date: date, p_code: code })

export const toucherEtape = (date: string, code: string, avancement?: Record<string, unknown>) =>
  rpc<Etape>('fc_etape_touch', { p_date: date, p_code: code, p_avancement: avancement ?? null })

export const validerEtape = (
  date: string, code: string, force = false, avancement?: Record<string, unknown>,
) => rpc<Etape>('fc_etape_valider', {
  p_date: date, p_code: code, p_force: force, p_avancement: avancement ?? null,
})

export const mettreDeCote = (date: string, code: string, motif: string) =>
  rpc<Etape>('fc_etape_de_cote', { p_date: date, p_code: code, p_motif: motif })

export const repondreQuestion = (date: string, code: string, oui: boolean) =>
  rpc<Etape>('fc_etape_reponse', { p_date: date, p_code: code, p_oui: oui })

export const rouvrirEtape = (date: string, code: string) =>
  rpc<Etape>('fc_etape_reouvrir', { p_date: date, p_code: code })

export const majBranche = (
  date: string, code: string, branche: 'dep' | 'sco',
  statut: 'a_faire' | 'en_cours' | 'validee' | 'skip_question', force = false,
) => rpc<Etape>('fc_etape_branche', {
  p_date: date, p_code: code, p_branche: branche, p_statut: statut, p_force: force,
})

export const recalculerRegles = (date: string) =>
  rpc<{ regles: Record<string, string>; changements: unknown[] }>('fc_recompute_regles', { p_date: date })

export const cloturerRun = (date: string) =>
  rpc<{ run: Run; stats: Record<string, number> }>('fc_cloturer_run', { p_date: date })

/** Rouvre une journée clôturée : fc_rouvrir_run rebascule fc_runs ET journees. */
export const rouvrirRun = (date: string) =>
  rpc<{ run: Run }>('fc_rouvrir_run', { p_date: date })

export const noterJournal = (date: string, note: string, etape?: string | null) =>
  rpc<{ ok: boolean }>('fc_note', { p_date: date, p_note: note, p_etape: etape ?? null })

// ── Écritures dans le blob V2 — RPC existants, jamais d'UPDATE direct ──────

/**
 * set_pad_verif (V2, migration 12) : écrit rapport.padVerifs[paymentId].
 * La RPC ne valide que `type` ∈ {carte, cheque} et stocke le reste tel quel —
 * c'est ce qui permet à FlashCP d'y ajouter `montant_reel` sans la modifier.
 */
export const enregistrerPadVerif = (
  date: string, paymentId: string, verif: Record<string, unknown> | null,
) => rpc<Record<string, unknown>>('set_pad_verif', {
  p_date: date, p_payment_id: paymentId, p_verif: verif,
})

/**
 * patch_rapport_merge (V2) : fusion atomique d'un patch dans `rapport`, sous
 * SELECT … FOR UPDATE. Refuse les journées clôturées et les clés en `_`.
 * À n'utiliser que là où aucune RPC typée n'existe.
 */
export const patcherRapport = (date: string, patch: Record<string, unknown>) =>
  rpc<void>('patch_rapport_merge', { p_date: date, p_patch: patch })

/** set_payment_verification (V2) : rapport.verifications[paymentId]. */
export const enregistrerVerification = (date: string, paymentId: string, statut: string | null) =>
  rpc<unknown>('set_payment_verification', {
    p_date: date, p_payment_id: paymentId, p_status: statut,
  })

/** set_ticket_status (V2) : validé = null · sinon { status }. Garde ticket_not_found. */
export const enregistrerStatutTicket = (
  date: string, kind: 'retours' | 'formation' | 'annules', ticket: string,
  statut: { status: string; motif?: string } | null,
) => rpc<unknown>('set_ticket_status', {
  p_date: date, p_kind: kind, p_ticket: String(ticket), p_status: statut,
})

/** set_operator_report (V2) : signalement caissier, auteur et heure posés par le serveur. */
export const enregistrerSignalement = (date: string, operateur: string | number, texte: string) =>
  rpc<Record<string, unknown>>('set_operator_report', {
    p_date: date, p_op: String(operateur), p_text: texte ?? '',
  })

/** mutate_paiement (V2) : add / edit / delete avec audit dans paiements_historique. */
export const muterPaiement = (
  date: string, action: 'add' | 'edit' | 'delete', paymentId: string,
  apres: Record<string, unknown> | null = null,
) => rpc<{ before: unknown; after: unknown }>('mutate_paiement', {
  p_date: date, p_action: action, p_payment_id: paymentId, p_after: apres,
})

/** save_apport (V2) : écriture atomique de l'apport d'un caissier. */
export const enregistrerApport = (
  date: string, opId: string, apport: Record<string, unknown> | null,
) => rpc<unknown>('save_apport', { p_date: date, p_op_id: String(opId), p_apport: apport })

/** save_inventaire_section (V2) : sauvegarde par section, anti-écrasement. */
export type SectionInventaire =
  | 'rouleaux' | 'pieces' | 'billets' | 'caisse_fixes' | 'entrees_coffre'
  | 'prelevements_j' | 'prelevements_j1' | 'solde_hier' | 'commentaire_ecart'

export const enregistrerSectionInventaire = (
  date: string, section: SectionInventaire, valeur: unknown,
) => rpc<unknown>('save_inventaire_section', { p_date: date, p_section: section, p_value: valeur })

/**
 * Validation de l'inventaire — le SERVEUR recalcule (fc_totaux_coffre, miroir
 * SQL de computeTotals), journalise toute divergence avec ce que le client
 * croyait, puis écrit SES chiffres via la RPC V2 set_inventaire_validation.
 */
export const validerInventaire = (
  date: string, totalCompte: number, soldeAttendu: number, ecart: number, commentaire: string,
) => rpc<{ total_compte: number; solde_attendu: number; ecart: number; divergences: unknown[] }>(
  'fc_valider_inventaire', {
    p_date: date, p_total_compte: totalCompte, p_solde_attendu: soldeAttendu,
    p_ecart: ecart, p_commentaire: commentaire,
  })

export const rouvrirInventaire = (date: string) =>
  rpc<{ ok: boolean }>('fc_rouvrir_inventaire', { p_date: date })

/** mobile_validate_ticket_resto (V2) : valide un TRP par sa référence scannée. */
export const validerTicketResto = (date: string, reference: string) =>
  rpc<{ id: string; ticket: string; montant: number }>('mobile_validate_ticket_resto', {
    p_date: date, p_reference: reference,
  })

/** mobile_mark_ticket_validated (V2) : valide un ticket retour / annulé / formation par son numéro. */
export const validerTicketParNumero = (date: string, ticket: string) =>
  rpc<{ category: string }>('mobile_mark_ticket_validated', {
    p_date: date, p_ticket_num: String(ticket),
  })

// ── PAD en attente ─────────────────────────────────────────────────────────

export async function chargerPadAttente(statut?: PadAttente['statut']): Promise<PadAttente[]> {
  let q = supabase.from('fc_pad_attente').select('*').order('cree_at', { ascending: false })
  if (statut) q = q.eq('statut', statut)
  const { data, error } = await q
  if (error) throw error
  return (data ?? []) as PadAttente[]
}

export const ajouterPadAttente = (
  date: string, ref: string, paymentId: string | null, montant: number,
) => rpc<PadAttente>('fc_pad_attente_ajouter', {
  p_date: date, p_pad_ref: ref, p_payment_id: paymentId, p_montant: montant,
})

export const resoudrePadAttente = (id: string, statut: PadAttente['statut'], note?: string) =>
  rpc<PadAttente>('fc_pad_attente_resoudre', { p_id: id, p_statut: statut, p_note: note ?? null })

// ── Collaboration (§8) ─────────────────────────────────────────────────────

export async function chargerMessages(limite = 60): Promise<Message[]> {
  const { data, error } = await supabase
    .from('fc_messages').select('*').order('at', { ascending: false }).limit(limite)
  if (error) throw error
  return ((data ?? []) as Message[]).reverse()
}

export async function posterMessage(args: {
  texte: string; auteurNom: string; journee?: string | null; etape?: string | null
}): Promise<Message> {
  const { data, error } = await supabase.from('fc_messages').insert({
    texte: args.texte, auteur_nom: args.auteurNom,
    journee_date: args.journee ?? null, etape_code: args.etape ?? null,
  }).select().single()
  if (error) throw error
  return data as Message
}

export async function chargerConsignes(dates: string[]): Promise<Consigne[]> {
  if (dates.length === 0) return []
  const { data, error } = await supabase
    .from('fc_consignes').select('*').in('journee_cible', dates)
    .order('urgence', { ascending: false }).order('at')
  if (error) throw error
  return (data ?? []) as Consigne[]
}

export async function creerConsigne(args: {
  journee: string; etape?: string | null; texte: string; urgence: 1 | 2 | 3; auteurNom: string
}): Promise<Consigne> {
  const { data, error } = await supabase.from('fc_consignes').insert({
    journee_cible: args.journee, etape_cible: args.etape ?? null,
    texte: args.texte, urgence: args.urgence, auteur_nom: args.auteurNom,
  }).select().single()
  if (error) throw error
  return data as Consigne
}

export async function majConsigne(
  id: string, statut: Consigne['statut'], acteurNom: string, motif?: string,
): Promise<Consigne> {
  const fait = statut === 'faite' || statut === 'de_cote'
  const { data, error } = await supabase.from('fc_consignes').update({
    statut,
    motif: motif ?? null,
    fait_par_nom: fait ? acteurNom : null,
    fait_at: fait ? new Date().toISOString() : null,
  }).eq('id', id).select().single()
  if (error) throw error
  return data as Consigne
}

/** Reporte une consigne au lendemain — elle reste une chose à faire (§8.2). */
export async function reporterConsigne(id: string, versDate: string): Promise<Consigne> {
  const { data, error } = await supabase.from('fc_consignes')
    .update({ journee_cible: versDate, statut: 'a_faire' })
    .eq('id', id).select().single()
  if (error) throw error
  return data as Consigne
}

// ── La boîte aux lettres des exports ORIKA (§ fc_imports) ──────────────────
//
// L'extension DÉPOSE le fichier brut, FlashCP le parse et l'écrit. C'est ce
// partage qui garantit un parseur unique : l'extension est installée à la main
// sur un poste, FlashCP se déploie depuis le serveur.

/**
 * Les quatre exports CSV, plus le ticket JSON « Vérification du tiroir » des
 * caisses automatiques — même boîte aux lettres, mais il nourrit l'étape 02A
 * et non le blob de l'étape 01.
 */
export type TypeImport =
  | 'reglements' | 'rapport_operateur' | 'retours' | 'operations' | 'verif_tiroir'
export type StatutImport = 'en_attente' | 'ingere' | 'ignore' | 'erreur'

export interface LigneImport {
  id: string
  journee_date: string
  type: TypeImport
  chemin: string
  fichier: string | null
  octets: number | null
  empreinte: string | null
  source: 'extension' | 'manuel' | 'autre'
  statut: StatutImport
  depose_at: string
  depose_par_nom: string | null
  ingere_at: string | null
  ingere_par_nom: string | null
  lignes: number | null
  message: string | null
}

const SEAU = 'fc-pieces'

/** `imports/<AAAA-MM-JJ>/<type>__<horodatage>__<8 hexa>.csv` — convention vérifiée en base. */
export function cheminImport(date: string, type: TypeImport, nom = 'export.csv'): string {
  const t = new Date().toISOString().replace(/[-:]/g, '').slice(0, 13)
  const alea = Math.random().toString(16).slice(2, 10)
  const ext = /\.[a-z0-9]{2,4}$/i.exec(nom)?.[0] ?? '.csv'
  return `imports/${date}/${type}__${t}__${alea}${ext}`
}

export async function listerImports(date: string, statut?: StatutImport): Promise<LigneImport[]> {
  let q = supabase.from('fc_imports').select('*').eq('journee_date', date)
    .order('depose_at', { ascending: false })
  if (statut) q = q.eq('statut', statut)
  const { data, error } = await q
  if (error) throw error
  return (data ?? []) as LigneImport[]
}

export async function televerserImport(chemin: string, contenu: Blob | File): Promise<void> {
  const { error } = await supabase.storage.from(SEAU)
    .upload(chemin, contenu, { contentType: 'text/csv', upsert: false })
  if (error) throw error
}

export async function telechargerImport(chemin: string): Promise<Uint8Array> {
  const { data, error } = await supabase.storage.from(SEAU).download(chemin)
  if (error) throw error
  return new Uint8Array(await data.arrayBuffer())
}

export const declarerImport = (args: {
  date: string; type: TypeImport; chemin: string
  fichier?: string | null; octets?: number | null; empreinte?: string | null
  source?: 'extension' | 'manuel' | 'autre'
}) => rpc<LigneImport & { deja_depose: boolean }>('fc_import_declarer', {
  p_date: args.date, p_type: args.type, p_chemin: args.chemin,
  p_fichier: args.fichier ?? null, p_octets: args.octets ?? null,
  p_empreinte: args.empreinte ?? null, p_source: args.source ?? 'extension',
})

export const majStatutImport = (
  id: string, statut: StatutImport, lignes?: number | null, message?: string | null,
) => rpc<LigneImport>('fc_import_statut', {
  p_id: id, p_statut: statut, p_lignes: lignes ?? null, p_message: message ?? null,
})

/** Le contenu JSON d'un dépôt (ticket « Vérification du tiroir »). */
export async function telechargerJson(chemin: string): Promise<unknown> {
  const octets = await telechargerImport(chemin)
  return JSON.parse(new TextDecoder('utf-8').decode(octets))
}

// ── Réglages lus depuis SotCP (jamais écrits ici) ──────────────────────────

export async function chargerReglage<T>(cle: string, defaut: T): Promise<T> {
  const { data, error } = await supabase
    .from('settings').select('value').eq('key', cle).maybeSingle()
  if (error) throw error
  return (data?.value as T | undefined) ?? defaut
}

// ── Réglages (back-office) ─────────────────────────────────────────────────
//
// L'écriture passe par UNE RPC `SECURITY DEFINER` et jamais par la table :
// `settings` est partagée avec SotCP V2 et V3, et sa RLS n'a pas à être
// desserrée pour que FlashCP puisse régler quoi que ce soit. La RPC valide la
// forme, refuse les clés qui ne se règlent pas d'ici, recalcule les clés
// dérivées et journalise le changement.

export interface Reglage { key: string; value: unknown; updated_at: string }

export async function listerReglages(): Promise<Reglage[]> {
  const { data, error } = await supabase.from('settings').select('key, value, updated_at')
  if (error) throw error
  return (data ?? []) as Reglage[]
}

/**
 * @param base l'horodatage lu au chargement — le serveur refuse (`reglage_perime`)
 *   si quelqu'un d'autre a écrit entre-temps, plutôt que d'effacer son travail.
 */
export const ecrireReglage = (cle: string, valeur: unknown, base?: string | null, note?: string) =>
  rpc<Reglage>('fc_reglage_ecrire', {
    p_cle: cle, p_valeur: valeur, p_base: base ?? null, p_note: note ?? null,
  })

/** Recalcule superviseurBadges et fdcZeroBadges depuis les fiches caissiers. */
export const resyncBadges = () => rpc<Reglage>('fc_reglages_resync_badges', {})

export interface LigneJournalReglage {
  id: number
  cle: string
  avant: unknown
  apres: unknown
  acteur_nom: string | null
  at: string
  note: string | null
}

export async function journalReglages(limite = 60): Promise<LigneJournalReglage[]> {
  const { data, error } = await supabase.from('fc_reglages_journal')
    .select('*').order('at', { ascending: false }).limit(limite)
  if (error) throw error
  return (data ?? []) as LigneJournalReglage[]
}

/**
 * Reporte le total du vidage SCO dans les entrées de coffre, en une seule
 * transaction serveur.
 *
 * L'argent sorti des automates entre au coffre : sans cette entrée, le solde
 * attendu est court de tout le vidage pendant que le comptage, lui, l'a vu — et
 * l'écart accuse un manque qui n'existe pas. La ligne porte un identifiant
 * stable : on la remplace, on ne l'empile pas.
 */
export const synchroniserEntreeSco = (date: string) =>
  rpc<{ applique: boolean; motif?: string; montant?: number; manuelles?: number }>(
    'fc_sco_vers_coffre', { p_date: date })

// ── Le versement des tampons Honeywell ─────────────────────────────────────
// L'appareil n'écrit que dans son tampon (migration 11) ; sans ces trois
// appels, tout ce qui est scanné y resterait. Le PC lit, révise, puis verse —
// et c'est lui, pas l'appareil, qui tient le verrou en écrivant.

/** Les tampons livrés que personne n'a encore repris. */
export const hwEnAttente = (date: string) =>
  rpc<SessionEnAttente[]>('fc_hw_en_attente', { p_date: date })

/** Ce que le versement FERAIT, ligne à ligne. Lecture seule, sans réserve. */
export const hwApercu = (date: string, session: string) =>
  rpc<Apercu>('fc_hw_apercu', { p_date: date, p_session: session })

/** `ignorer` porte les lignes décochées : réviser sans pouvoir refuser ne serait que regarder. */
export const hwVerser = (date: string, session: string, ignorer: string[] = []) =>
  rpc<{ session: string; module: string; ecrits: number; passes: number
        resume: Record<string, number>; controle: Record<string, unknown> }>(
    'fc_hw_verser', { p_date: date, p_session: session, p_ignorer: ignorer })

/**
 * Ce qu'un code-barres devient, selon les formats réglés — la MÊME fonction que
 * celle qu'interroge le terminal. Le back-office s'en sert comme d'un banc
 * d'essai : on colle un vrai scan, on voit dans quelle famille il tombe.
 *
 * Le calcul est côté serveur et n'a pas de jumeau ici : deux implémentations de
 * la reconnaissance auraient dérivé au premier format ajouté, et le bon serait
 * parti dans la mauvaise famille sans que rien ne le signale.
 */
// ── La trappe d'administration (/backdoor) ─────────────────────────────────
// Hors parcours, hors navigation. Ces RPC ignorent volontairement les verrous
// que le reste de l'app respecte (journée clôturée, jours ouverts, clés en `_`)
// — c'est le sens de la trappe. Elles restent derrière la connexion et
// journalisent chaque écriture ; voir supabase/35_fc_backdoor.sql.

export interface LigneBrute {
  existe: boolean
  date: string
  status: string
  closed_at: string | null
  created_at: string | null
  updated_at: string | null
  archived_at: string | null
  rapport: Record<string, unknown>
}

/** La ligne brute d'une journée, ou null si elle n'existe pas encore. */
// ── Récupération : les sauvegardes nocturnes (bucket privé fc-backups) ──────
// Réservé aux admins par la RLS. On liste et on télécharge — restaurer reste un
// geste serveur, jamais un clic depuis le site.

export interface Sauvegarde { chemin: string; taille: number; cree: string }

export async function listerSauvegardes(): Promise<Sauvegarde[]> {
  const { data, error } = await supabase.storage.from('fc-backups')
    .list('nightly', { limit: 100, sortBy: { column: 'created_at', order: 'desc' } })
  if (error) throw error
  return (data ?? [])
    .filter((o) => o.name && !o.name.startsWith('.'))
    .map((o) => ({
      chemin: `nightly/${o.name}`,
      taille: (o.metadata?.size as number | undefined) ?? 0,
      cree: o.created_at ?? '',
    }))
}

/** URL signée (1 h) pour télécharger une sauvegarde. */
export async function lienSauvegarde(chemin: string): Promise<string> {
  const { data, error } = await supabase.storage.from('fc-backups').createSignedUrl(chemin, 3600)
  if (error) throw error
  return data.signedUrl
}

export const backdoorLire = (date: string) =>
  rpc<LigneBrute | null>('fc_backdoor_lire', { p_date: date })

/**
 * Réécrit ce qui est fourni. `closedAt` : null = ne pas toucher, '' = effacer,
 * une chaîne = poser. `rapport` remplace TOUT le blob.
 */
export const backdoorEcrire = (args: {
  date: string
  rapport?: Record<string, unknown>
  status?: string
  closedAt?: string | null
  note?: string
}) => rpc<LigneBrute>('fc_backdoor_ecrire', {
  p_date: args.date,
  p_rapport: args.rapport ?? null,
  p_status: args.status ?? null,
  p_closed_at: args.closedAt === undefined ? null : args.closedAt,
  p_note: args.note ?? null,
})

/** Crée une journée vide à n'importe quelle date (idempotent). */
export const backdoorCreer = (date: string) =>
  rpc<LigneBrute>('fc_backdoor_creer', { p_date: date })

/** Déplace une journée entière vers une date libre. */
export const backdoorRedater = (de: string, vers: string) =>
  rpc<LigneBrute>('fc_backdoor_redater', { p_de: de, p_vers: vers })

/**
 * Écrit un réglage BRUT depuis la trappe — sans la validation de
 * fc_reglage_ecrire (caisson unique, plomb, clés 0–5). Recalcule quand même les
 * badges dérivés pour `caissiers`, et journalise. Voir 36_fc_backdoor_reglage.sql.
 */
export const backdoorReglageEcrire = (cle: string, valeur: unknown, note?: string) =>
  rpc<{ cle: string; valeur: unknown }>('fc_backdoor_reglage_ecrire', {
    p_cle: cle, p_valeur: valeur, p_note: note ?? null,
  })

export const reconnaitreCode = (code: string) =>
  rpc<{ famille: string; reglement?: string; montant_a_saisir: boolean
        cle?: string; cents_code?: number; texte: string }>(
    'fc_reconnaitre_code', { p_code: code })
